/**
 * Markdown Parser
 *
 * Single Responsibility: Parses markdown content into structured blocks
 * Open/Closed Principle: Can be extended with new block types without modifying existing code
 */

export interface ContentBlock {
  type: 'paragraph' | 'heading' | 'list' | 'nested-list';
  content: string | { text: string; children?: string[] }[];
  level?: number;
}

export class MarkdownParser {
  /**
   * Parse markdown content into structured blocks
   */
  static parse(content: string): ContentBlock[] {
    const blocks: ContentBlock[] = [];
    const lines = content.split('\n');
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // Skip empty lines
      if (!trimmed) {
        i++;
        continue;
      }

      // Check for markdown headings (###)
      if (trimmed.match(/^#{1,6}\s+/)) {
        const level = (trimmed.match(/^#+/) || [''])[0].length;
        const headingText = trimmed.replace(/^#{1,6}\s+/, '');
        blocks.push({
          type: 'heading',
          content: headingText,
          level,
        });
        i++;
        continue;
      }

      // Check for bullet points with potential nesting
      if (trimmed.match(/^[*•-]\s+/)) {
        const listItems: { text: string; children?: string[] }[] = [];

        while (i < lines.length) {
          const currentLine = lines[i];
          const currentTrimmed = currentLine.trim();

          // Break if we hit a non-list line
          if (!currentTrimmed || !currentTrimmed.match(/^[*•-\s]/)) {
            break;
          }

          const indent = currentLine.search(/\S/);

          // Root level bullet (indent 0-4)
          if (indent <= 4 && currentTrimmed.match(/^[*•-]\s+/)) {
            const itemText = currentTrimmed.replace(/^[*•-]\s+/, '');
            const children: string[] = [];

            // Look ahead for nested bullets
            let j = i + 1;
            while (j < lines.length) {
              const nextLine = lines[j];
              const nextTrimmed = nextLine.trim();
              const nextIndent = nextLine.search(/\S/);

              // Check if it's a nested bullet (more indentation)
              if (nextIndent > 4 && nextTrimmed.match(/^[*•-]\s+/)) {
                children.push(nextTrimmed.replace(/^[*•-]\s+/, ''));
                j++;
              } else if (!nextTrimmed) {
                // Skip empty lines within list
                j++;
                continue;
              } else {
                break;
              }
            }

            listItems.push({
              text: itemText,
              children: children.length > 0 ? children : undefined,
            });
            i = j;
          } else {
            i++;
          }
        }

        if (listItems.length > 0) {
          blocks.push({
            type: 'nested-list',
            content: listItems,
          });
        }
        continue;
      }

      // Regular paragraph
      blocks.push({
        type: 'paragraph',
        content: trimmed,
      });
      i++;
    }

    return blocks;
  }
}
